/**
 * Phase 15.5 P1 — Contact Producer Dialog
 *
 * Shows the producer's available contact channels (email, Instagram,
 * Discord, Website) as actionable buttons. Each channel opens the
 * native handler (mailto, IG profile, etc.) in a new tab — no new
 * messaging infra needed for MVP.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const linkForInstagram = (handleOrUrl) => {
  if (!handleOrUrl) return null;
  if (handleOrUrl.startsWith("http://") || handleOrUrl.startsWith("https://")) return handleOrUrl;
  const h = handleOrUrl.replace(/^@/, "");
  return `https://instagram.com/${encodeURIComponent(h)}`;
};

const linkForDiscord = (handleOrUrl) => {
  if (!handleOrUrl) return null;
  if (handleOrUrl.startsWith("http")) return handleOrUrl;
  // Discord doesn't have a user-by-handle URL; show the handle for the
  // artist to copy. The button uses navigator.clipboard.
  return null;
};

const linkForWebsite = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
};

const ContactRow = ({ icon, label, value, href, onCopy, testId }) => (
  <a
    href={href || "#"}
    target={href ? "_blank" : undefined}
    rel={href ? "noopener noreferrer" : undefined}
    onClick={onCopy ? (e) => { e.preventDefault(); onCopy(value); } : undefined}
    className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition"
    data-testid={testId}
  >
    <div className="flex items-center gap-3 min-w-0">
      <span className="text-2xl">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</div>
        <div className="text-sm text-white font-bold truncate">{value}</div>
      </div>
    </div>
    <span className="text-pink-300 text-sm font-bold shrink-0">
      {onCopy ? "Copy →" : "Open →"}
    </span>
  </a>
);

const ContactProducerDialog = ({ open, onOpenChange, beat }) => {
  if (!beat) return null;
  const producer = beat.producer || {};
  const producerName = producer.artist_name || producer.name || "this producer";

  const channels = [
    beat.contact_email && {
      icon: "✉", label: "Email", value: beat.contact_email,
      href: `mailto:${encodeURIComponent(beat.contact_email)}?subject=${encodeURIComponent("Beat License Inquiry — " + (beat.title || ""))}`,
      testId: "contact-email",
    },
    beat.contact_instagram && {
      icon: "📷", label: "Instagram", value: beat.contact_instagram,
      href: linkForInstagram(beat.contact_instagram), testId: "contact-instagram",
    },
    beat.contact_discord && (() => {
      const url = linkForDiscord(beat.contact_discord);
      return {
        icon: "🎮", label: "Discord", value: beat.contact_discord,
        href: url, onCopy: !url ? (v) => navigator.clipboard?.writeText(v) : null,
        testId: "contact-discord",
      };
    })(),
    beat.contact_website && {
      icon: "🌐", label: "Website", value: beat.contact_website,
      href: linkForWebsite(beat.contact_website), testId: "contact-website",
    },
  ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-gradient-to-br from-[#1a0729] via-[#0f0518] to-black text-white border-white/15 max-w-md"
        data-testid="contact-producer-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-black">
            Contact <span className="text-pink-300">{producerName}</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Reach out about “{beat.title}” to license, collab, or ask a question.
          </DialogDescription>
        </DialogHeader>

        {channels.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-300" data-testid="contact-no-channels">
            This producer hasn't added contact info yet.
          </div>
        ) : (
          <div className="space-y-2 pt-1">
            {channels.map((ch) => (
              <ContactRow key={ch.label} {...ch} />
            ))}
          </div>
        )}

        <p className="text-[10px] text-gray-500 text-center pt-2">
          Tip: licensing terms vary by producer — confirm rights before releasing.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ContactProducerDialog;
